// Стор воркспейса курса (Course Workspace V2, §4.3 + §4.7, декрет E12).
//
// persist — только пользовательские предпочтения раскладки: открытость панелей,
// активные табы, ширины. Эфемерное (какой курс/тема открыты, мобильная шторка,
// известная ширина окна, флаги авто-сворачивания) в partialize НЕ попадает —
// правило frontend-state.md §2 (иначе после reload «прилипает» чужой курс).
//
// Геометрия выводится из ОДНОГО числа `COURSE_SIDE` (= токен --course-side-width
// в apps/web/src/styles.css): левая панель = 392 − 64 (свёрнутый рельс
// приложения) = 328. Ресайз пользователем ширину меняет осознанно, двойной клик
// по разделителю возвращает дефолт.
//
// ПРАВОЙ КОЛОНКИ У ВОРКСПЕЙСА БОЛЬШЕ НЕТ (план единого репетитора, этап 3):
// репетитор — глобальная панель приложения, инструменты и артефакты переехали
// во вкладки левой панели. Поэтому здесь нет ни `rightOpen/rightWidth/rightTab`,
// ни `toolsSplit` (доля витрины инструментов внутри правой колонки). Записи
// прошлых поколений в localStorage эти ключи ещё содержат — `mergePersisted`
// их просто НЕ ЧИТАЕТ, а `partialize` перестаёт писать: миграция не нужна и
// была бы опаснее (ошибка в ней стоит сброса настроенной ширины панели).
//
// Clamp живёт здесь (как в stores/side-panel-width.ts), а не в компоненте:
// ручка ресайза шлёт сырое значение от курсора.
import { create } from "zustand";
import { persist } from "zustand/middleware";

/** Токен ширины боковой панели курса (px). Зеркалит --course-side-width. */
const COURSE_SIDE = 392;
/** Ширина свёрнутого рельса приложения (main-layout.tsx: 64, sidebar.tsx: w-16). */
export const COURSE_APP_RAIL_WIDTH = 64;

export const COURSE_LEFT_DEFAULT_WIDTH = COURSE_SIDE - COURSE_APP_RAIL_WIDTH; // 328

/**
 * Доля высоты левой панели, отданная МАРШРУТУ ТЕМ (остальное — инструменты).
 * Дефолт 0.5 ⇒ план и инструменты делят высоту пополам.
 */
export const COURSE_LEFT_SPLIT_DEFAULT = 0.5;
/**
 * Границы доли маршрута. Обе стороны сплита обязаны оставаться читаемыми:
 * • ниже 0.45 при 20+ темах маршрут превращается в щель, а он — главный
 *   навигационный орган курса (риск прямо назван в плане §4.7);
 * • выше 0.85 инструменты схлопываются в полоску, а сворачивания у секции
 *   НЕТ намеренно (discoverability > экономия пикселей, там же) — то есть
 *   «почти нулевая» доля стала бы скрытым сворачиванием чёрным ходом.
 */
export const COURSE_LEFT_SPLIT_MIN = 0.45;
export const COURSE_LEFT_SPLIT_MAX = 0.85;

/**
 * Доля высоты вкладки «Источники» под зоной ДОБАВЛЕНИЯ (дроп + кнопки + веб-поиск
 * с результатами). Остаток — уже добавленные источники. Дефолт 0.5 ⇒ пополам,
 * как у сплита «План ↔ инструменты».
 */
export const COURSE_SOURCES_SPLIT_DEFAULT = 0.5;
/** Границы доли «добавления»: обе половины остаются читаемыми. */
export const COURSE_SOURCES_SPLIT_MIN = 0.35;
export const COURSE_SOURCES_SPLIT_MAX = 0.75;

export const COURSE_LEFT_MIN_WIDTH = 280;
export const COURSE_LEFT_MAX_WIDTH = 480;
/**
 * Комфортная ширина центральной сцены: пока окно позволяет, панели держатся в
 * своих предпочтениях, а всё лишнее забирает центр.
 */
export const COURSE_CENTER_MIN_WIDTH = 560;
/**
 * Ширина центра, ниже которой сцену не жмём даже на самом узком desktop.
 *
 * Число не произвольное — оно замыкает бюджет узкого экрана:
 * `64 (рельс) + 280 (панель курса) + 360 (центр) + 320 (репетитор) = 1024`,
 * то есть ровно нижняя граница desktop-композиции (`BREAKPOINTS.lg`). Пока
 * равенство держится, на desktop НИ ОДНА колонка не обязана сворачиваться —
 * достаточно сжать. Инвариант сторожит `_panel-geometry.test.ts`.
 */
export const COURSE_CENTER_COMPRESSED_MIN_WIDTH = 360;

/**
 * Табы левой панели курса. ЕДИНСТВЕННОЕ объявление: `components/courses/
 * workspace/_scene.ts` его РЕЭКСПОРТИРУЕТ, а не дублирует.
 *
 * Раньше тип жил двумя независимыми копиями, и расширение одной из них
 * молча расходилось со второй. Направление зависимости выбрано по границам
 * фронта (`rules/module-boundaries.md`): `components/` → `stores/` разрешено,
 * обратное — нет, поэтому источником стал стор (здесь же лежит persist табов).
 */
export type CourseLeftTab = "topics" | "sources" | "created";
/**
 * Мобильная шторка (< lg): то, что на desktop стоит колонкой, вызывается
 * полосой вызова и открывается шторкой.
 *
 * Вкладки панели становятся шторками автоматически (тип производный), плюс
 * `"tools"` — витрина инструментов. Она НЕ вкладка панели: на desktop
 * инструменты живут секцией под маршрутом тем, а на телефоне у них своя
 * кнопка в полосе вызова, поэтому и своя шторка. Раньше та кнопка уводила на
 * полноэкранную сцену `?view=tools` — человек терял из вида шаг темы ради
 * списка плиток (жалоба 2026-08-13).
 *
 * Репетитора в этом типе НЕТ: он открывается глобальной шторкой приложения
 * (`stores/side-panel.ts`).
 */
export type CourseWorkspaceSheet = CourseLeftTab | "tools" | null;

const COURSE_LEFT_TABS: ReadonlySet<string> = new Set<CourseLeftTab>([
	"topics",
	"sources",
	"created",
]);

/**
 * Значение — вкладка панели курса.
 *
 * Экспортируется: тип шторки шире вкладок (`"tools"`), и потребителям нужен
 * способ отличить «шторка = вкладка панели» от «шторка со своим содержимым»,
 * не перечисляя вкладки второй раз.
 */
export function isCourseLeftTab(value: unknown): value is CourseLeftTab {
	return typeof value === "string" && COURSE_LEFT_TABS.has(value);
}

interface CourseWorkspaceGeometry {
	leftOpen: boolean;
	leftWidth: number;
	/** Панель свернули МЫ под узкое окно (можно вернуть при расширении). */
	autoCollapsedLeft: boolean;
	/**
	 * Последняя известная ДОСТУПНАЯ ширина (окно за вычетом открытой панели
	 * репетитора); 0 = ещё не измеряли.
	 */
	viewportWidth: number;
}

interface CourseWorkspaceState extends CourseWorkspaceGeometry {
	// ── persist ────────────────────────────────────────────────────────────
	leftTab: CourseLeftTab;

	// ── эфемерное ──────────────────────────────────────────────────────────
	courseId: string | null;
	activeTopicId: string | null;
	openSheet: CourseWorkspaceSheet;
	/**
	 * Курс, для которого вкладка входа УЖЕ выбрана.
	 *
	 * Отдельно от `courseId`, потому что вход и решение о вкладке случаются в
	 * разные моменты: воркспейс монтируется на локальной копии PowerSync, где
	 * секций может ещё не быть, и решать по такой копии нельзя — см.
	 * `resolveEntryTab`.
	 */
	entryTabCourseId: string | null;

	// ── действия ───────────────────────────────────────────────────────────
	setLeftOpen: (open: boolean) => void;
	toggleLeft: () => void;
	setLeftTab: (tab: CourseLeftTab) => void;
	/**
	 * Доля высоты левой панели под МАРШРУТОМ ТЕМ; остаток забирают инструменты.
	 * Пользовательское предпочтение (persist), как и ширины панелей: кому-то
	 * нужен весь маршрут, кому-то — инструменты под рукой.
	 */
	leftSplit: number;
	setLeftSplit: (fraction: number) => void;
	resetLeftSplit: () => void;
	/**
	 * Доля высоты вкладки «Источники» под зоной добавления; остаток — список
	 * уже добавленных. Отдельный ключ от `leftSplit`: пропорции Плана и
	 * Источников не должны драться.
	 */
	sourcesSplit: number;
	setSourcesSplit: (fraction: number) => void;
	resetSourcesSplit: () => void;
	setLeftWidth: (width: number) => void;
	resetLeftWidth: () => void;
	/**
	 * Сообщить стору доступную ширину (окно за вычетом открытой панели
	 * репетитора). Стор решает ТОЛЬКО «влезает ли панель вообще»; сколько именно
	 * пикселей ей досталось — считает оболочка (`_panel-geometry.ts`), потому что
	 * торг за пиксели идёт между тремя колонками сразу.
	 */
	applyViewport: (availableWidth: number) => void;
	/**
	 * Вход в курс. `hasProgram` — есть ли у курса собранная программа: от этого
	 * зависит вкладка, на которой открывается панель (см. `resolveEntryTab`).
	 * Вызывается и при смене темы внутри курса — тогда вкладку не трогаем.
	 */
	setCourse: (
		courseId: string | null,
		activeTopicId?: string | null,
		options?: { hasProgram?: boolean; programKnown?: boolean },
	) => void;
	/**
	 * Программа курса ПОЯВИЛАСЬ (черновик собрался в этой же сессии).
	 *
	 * Единственный случай, когда вкладку меняем не на входе: человек смотрел на
	 * «Источники», потому что смотреть было больше не на что, — а теперь есть
	 * план, и о его существовании он может просто не знать (жалоба владельца
	 * 2026-08-16). Переключаем ТОЛЬКО с «Источников»: уход с «Созданного» был бы
	 * уже вырыванием из работы, а не подсказкой.
	 */
	markProgramBuilt: (courseId: string) => void;
	setActiveTopic: (topicId: string | null) => void;
	setOpenSheet: (sheet: CourseWorkspaceSheet) => void;
}

/**
 * Зажать долю маршрута тем. Нечисло (NaN/Infinity прилетают из ручки ресайза,
 * когда высота контейнера ещё 0 и деление даёт мусор) трактуем как «значения
 * нет» → дефолт, а не как 0, иначе первый же кадр схлопнул бы маршрут.
 */
function clampLeftSplit(fraction: number): number {
	if (!Number.isFinite(fraction)) return COURSE_LEFT_SPLIT_DEFAULT;
	return Math.min(COURSE_LEFT_SPLIT_MAX, Math.max(COURSE_LEFT_SPLIT_MIN, fraction));
}

function clampSourcesSplit(fraction: number): number {
	if (!Number.isFinite(fraction)) return COURSE_SOURCES_SPLIT_DEFAULT;
	return Math.min(COURSE_SOURCES_SPLIT_MAX, Math.max(COURSE_SOURCES_SPLIT_MIN, fraction));
}

function clampRange(value: number, min: number, max: number): number {
	if (!Number.isFinite(value)) return min;
	return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Влезает ли панель курса рядом со сценой ХОТЯ БЫ на своём минимуме.
 *
 * Ширину ОТКРЫТОЙ панели репетитора здесь не вычитаем: она живёт вне каркаса
 * курса и отжимает его целиком (`marginRight` у `<main>`), поэтому приходит
 * сюда уже вычтенной (`availableWidth` из `main-layout.tsx`).
 *
 * Сравниваем именно с МИНИМУМАМИ, а не с дефолтами: на узком окне колонки
 * сжимаются (`lib/panel-shrink.ts`), и мерить «влезает ли предпочтение» значило
 * бы сворачивать панель там, где её достаточно было ужать. Ровно этим и был
 * порог 1351px до правки 2026-08-15.
 */
function panelFits(availableWidth: number): boolean {
	return (
		availableWidth - COURSE_APP_RAIL_WIDTH - COURSE_LEFT_MIN_WIDTH >=
		COURSE_CENTER_COMPRESSED_MIN_WIDTH
	);
}

/** Потолок ширины панели, при котором центр ещё не уже своего жёсткого минимума. */
function widthCeiling(g: CourseWorkspaceGeometry): number {
	if (g.viewportWidth <= 0) return Number.POSITIVE_INFINITY;
	return g.viewportWidth - COURSE_APP_RAIL_WIDTH - COURSE_CENTER_COMPRESSED_MIN_WIDTH;
}

function clampLeftWidth(raw: number, g: CourseWorkspaceGeometry): number {
	const base = clampRange(raw, COURSE_LEFT_MIN_WIDTH, COURSE_LEFT_MAX_WIDTH);
	return Math.max(COURSE_LEFT_MIN_WIDTH, Math.min(base, widthCeiling(g)));
}

/**
 * Авто-сворачивание панели курса — последняя мера, а не первая.
 *
 * Сначала колонки сжимаются (оболочка, `lib/panel-shrink.ts`); сворачиваем
 * только если панель не помещается даже на минимуме. На desktop (≥1024px) это
 * не наступает по построению — сумма минимумов равна 1024, — то есть ветка
 * работает страховкой на экзотику (браузерный zoom, будущая правка минимумов),
 * а не штатным режимом. Ниже 1024 композиция мобильная: боковые колонки не
 * рендерятся вовсе, и стор туда ширину не получает.
 *
 * Флаг `autoCollapsedLeft` отделяет «свернули мы» от «свернул пользователь»:
 * ручной выбор авто-возврат не перебивает.
 */
function resolveAutoCollapse(
	g: CourseWorkspaceGeometry,
	availableWidth: number,
): CourseWorkspaceGeometry {
	const next: CourseWorkspaceGeometry = { ...g, viewportWidth: availableWidth };
	const fits = panelFits(availableWidth);

	if (next.autoCollapsedLeft && fits) {
		next.leftOpen = true;
		next.autoCollapsedLeft = false;
	}
	if (!fits && next.leftOpen) {
		next.leftOpen = false;
		next.autoCollapsedLeft = true;
	}
	return next;
}

/**
 * Вкладка панели, на которой открывается КУРС.
 *
 * `leftTab` персистится (это предпочтение раскладки) и потому переносился между
 * курсами и через перезагрузку: стоило один раз оказаться на «Источниках» —
 * после создания курса или через «+ → добавить источники» в репетиторе, — и
 * любой следующий курс встречал пустой зоной загрузки вместо своей программы,
 * даже когда программа давно собрана и пройдена наполовину (жалоба 2026-08-15).
 *
 * Правило входа: программы ещё нет — «Источники» (в пустом курсе это
 * единственное осмысленное действие); программа есть — «План». Возврат к
 * персисту здесь намеренный: вкладка панели — это состояние РАБОТЫ С КУРСОМ, и
 * переносить его между разными курсами смысла не имеет.
 *
 * Внутри уже открытого курса (тот же `courseId` — смена темы, вида сцены)
 * выбор пользователя не трогаем.
 */
function resolveEntryTab(
	current: CourseWorkspaceState,
	courseId: string | null,
	hasProgram: boolean,
	programKnown: boolean,
): CourseLeftTab {
	if (courseId === null || current.entryTabCourseId === courseId) return current.leftTab;
	// Программа ещё не подтверждена — решение откладываем. Воркспейс монтируется
	// на локальной копии PowerSync раньше серверного ответа, и в этот момент у
	// курса с восемью темами `sections` пуст. Решив по такому кадру, мы
	// открывали «Источники» и залипали на них навсегда: повторный прогон
	// упирался в сравнение courseId и вкладку уже не трогал (жалоба владельца
	// 2026-08-16 — курс из каталога каждый раз встречал пустой зоной загрузки).
	if (!programKnown) return current.leftTab;
	return hasProgram ? "topics" : "sources";
}

/** Решение о вкладке входа принято — фиксируем курс, чтобы не пересматривать. */
function resolveEntryTabOwner(
	current: CourseWorkspaceState,
	courseId: string | null,
	programKnown: boolean,
): string | null {
	if (courseId === null) return current.entryTabCourseId;
	if (current.entryTabCourseId === courseId) return courseId;
	return programKnown ? courseId : current.entryTabCourseId;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function readBoolean(source: Record<string, unknown>, key: string): boolean | undefined {
	const value = source[key];
	return typeof value === "boolean" ? value : undefined;
}

function readNumber(source: Record<string, unknown>, key: string): number | undefined {
	const value = source[key];
	return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/**
 * Слияние сохранённого состояния с дефолтами.
 *
 * Ключ `studyhub:course-workspace` уже лежит в localStorage у пользователей, и
 * там записи РАЗНЫХ поколений стора: без `leftSplit`; с `rightOpen` /
 * `rightWidth` / `rightTab` / `toolsSplit` от снятой правой колонки; после
 * отката/A-B — потенциально с `leftTab`, которого в текущем наборе нет.
 * Дефолтный shallow-merge zustand перенёс бы их как есть: панель открывается на
 * несуществующем табе, а в сторе живут поля, которых больше не существует.
 *
 * Читаем ТОЛЬКО известные ключи: поля снятой колонки просто не переносятся
 * (и перестают писаться `partialize`), а сохранённая ширина левой панели
 * остаётся нетронутой — регидратация ничего не сбрасывает.
 *
 * Сознательно НЕ вводим `version` + `migrate`: версии в этом ключе никогда не
 * было, то есть у всех сохранённых записей `version: 0`, и любой bump обязал
 * бы писать миграцию ради удаления полей — при этом ошибка в ней стоит сброса
 * уже настроенной ширины панели. Нормализация на чтении даёт тот же результат.
 */
function mergePersisted(persisted: unknown, current: CourseWorkspaceState): CourseWorkspaceState {
	if (!isRecord(persisted)) return current;

	return {
		...current,
		leftOpen: readBoolean(persisted, "leftOpen") ?? current.leftOpen,
		// Незнакомый таб (старое поколение стора / ручная правка localStorage) —
		// не «пустая панель», а маршрут тем: это дефолтный экран курса.
		leftTab: isCourseLeftTab(persisted.leftTab) ? persisted.leftTab : current.leftTab,
		leftWidth: readNumber(persisted, "leftWidth") ?? current.leftWidth,
		// Доля пришла из прошлой версии границ — зажимаем текущими, иначе
		// сохранённое значение обошло бы clamp мимо `setLeftSplit`.
		// Старые заводские дефолты (0.7 / 0.63) поднимаем до 0.5 — иначе persist
		// оставил бы тесное окно инструментов после смены пропорции.
		leftSplit: clampLeftSplit(
			(() => {
				const saved = readNumber(persisted, "leftSplit");
				if (saved === 0.7 || saved === 0.63) return COURSE_LEFT_SPLIT_DEFAULT;
				return saved ?? current.leftSplit;
			})(),
		),
		sourcesSplit: clampSourcesSplit(readNumber(persisted, "sourcesSplit") ?? current.sourcesSplit),
	};
}

export const useCourseWorkspaceStore = create<CourseWorkspaceState>()(
	persist(
		(set, get) => ({
			leftOpen: true,
			leftTab: "topics",
			entryTabCourseId: null,
			leftWidth: COURSE_LEFT_DEFAULT_WIDTH,
			leftSplit: COURSE_LEFT_SPLIT_DEFAULT,
			sourcesSplit: COURSE_SOURCES_SPLIT_DEFAULT,

			autoCollapsedLeft: false,
			viewportWidth: 0,
			courseId: null,
			activeTopicId: null,
			openSheet: null,

			// Ручное открытие/закрытие снимает авто-флаг: осознанный выбор
			// пользователя не перебивается авто-возвратом при ресайзе окна.
			setLeftOpen: (open) => set({ leftOpen: open, autoCollapsedLeft: false }),
			toggleLeft: () => set((s) => ({ leftOpen: !s.leftOpen, autoCollapsedLeft: false })),

			setLeftTab: (tab) => set({ leftTab: tab }),

			setLeftSplit: (fraction) => set({ leftSplit: clampLeftSplit(fraction) }),
			resetLeftSplit: () => set({ leftSplit: COURSE_LEFT_SPLIT_DEFAULT }),

			setSourcesSplit: (fraction) => set({ sourcesSplit: clampSourcesSplit(fraction) }),
			resetSourcesSplit: () => set({ sourcesSplit: COURSE_SOURCES_SPLIT_DEFAULT }),

			setLeftWidth: (width) => set({ leftWidth: clampLeftWidth(width, get()) }),
			resetLeftWidth: () => set({ leftWidth: clampLeftWidth(COURSE_LEFT_DEFAULT_WIDTH, get()) }),

			applyViewport: (availableWidth) => {
				if (!Number.isFinite(availableWidth) || availableWidth <= 0) return;
				set(resolveAutoCollapse(get(), availableWidth));
			},

			setCourse: (courseId, activeTopicId = null, options) =>
				set((s) => {
					const programKnown = options?.programKnown ?? true;
					return {
						courseId,
						activeTopicId,
						leftTab: resolveEntryTab(s, courseId, options?.hasProgram ?? false, programKnown),
						entryTabCourseId: resolveEntryTabOwner(s, courseId, programKnown),
					};
				}),
			markProgramBuilt: (courseId) =>
				set((s) =>
					s.courseId === courseId && s.leftTab === "sources"
						? { leftTab: "topics", entryTabCourseId: courseId }
						: {},
				),
			setActiveTopic: (topicId) => set({ activeTopicId: topicId }),
			setOpenSheet: (sheet) => set({ openSheet: sheet }),
		}),
		{
			name: "studyhub:course-workspace",
			// partialize обязателен: courseId / activeTopicId / openSheet /
			// viewportWidth / авто-флаги — эфемерные, их
			// persist ломает вход в курс (и «прилепил» бы ширину узкого окна).
			partialize: (state) => ({
				leftOpen: state.leftOpen,
				leftTab: state.leftTab,
				leftWidth: state.leftWidth,
				leftSplit: state.leftSplit,
				sourcesSplit: state.sourcesSplit,
			}),
			merge: mergePersisted,
		},
	),
);
